import React, { useMemo, useState } from "react";
import Reveal from "./Reveal";
import Counter from "./Counter";
import {
  projects,
  partners,
  categoryCounts,
  ongoingCount,
  projectStats,
} from "../../data/projects";
import { MapPin, Building, ArrowUpRight, Search } from "lucide-react";

const stats = [
  { value: projectStats.totalProjects, label: "Documented Projects", suffix: "" },
  { value: projectStats.yearsActive, label: "Years Active", suffix: "+" },
  { value: projectStats.cities, label: "Locations Served", suffix: "" },
  { value: projectStats.totalClients, label: "Distinct Clients", suffix: "" },
];

export const Projects = () => {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let list = projects;
    if (filter === "Ongoing") {
      list = list.filter((p) => p.status === "Ongoing");
    } else if (filter !== "All") {
      list = list.filter((p) => p.category === filter);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.client.toLowerCase().includes(q) ||
          (p.location || "").toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [filter, query]);

  const chips = [
    { key: "All", count: projects.length },
    ...categoryCounts,
    { key: "Ongoing", count: ongoingCount },
  ];

  return (
    <section id="projects" data-testid="projects-section" className="relative py-28 md:py-40 bg-ink-900">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold">
                / 04 — Selected Works
              </span>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.02] tracking-tight">
                Projects executed across <span className="italic text-gold">Maharashtra&rsquo;s</span> industrial spine.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <Reveal delay={200}>
              <p className="text-base text-steel-light leading-relaxed">
                A documented portfolio of plumbing and sanitary infrastructure delivered for
                some of India&rsquo;s most respected manufacturers, in association with our
                long-standing construction partners.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Stat strip */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {stats.map((s, i) => (
            <div key={s.label} className="bg-ink-800 p-6 md:p-8" data-testid={`project-stat-${i}`}>
              <div className="font-display text-4xl md:text-5xl text-gold tracking-tight">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-steel-light">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Filter + Search */}
        <div className="mt-16 border-y border-white/5 py-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            {chips.map((c) => {
              const active = filter === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`project-filter-${c.key.toLowerCase()}`}
                  onClick={() => setFilter(c.key)}
                  className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-medium transition-all duration-300 border ${
                    active
                      ? "bg-gold text-ink-900 border-gold"
                      : "text-steel-light border-white/10 hover:border-gold hover:text-gold"
                  }`}
                >
                  {c.key}
                  <span className="ml-2 font-mono text-[9px] opacity-70">{c.count}</span>
                </button>
              );
            })}
          </div>

          <label
            className="flex items-center gap-3 border border-white/10 px-4 py-2.5 focus-within:border-gold transition-colors lg:min-w-[280px]"
            data-testid="project-search-wrapper"
          >
            <Search size={14} className="text-steel-light" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search client, location, category…"
              data-testid="project-search-input"
              className="bg-transparent border-0 outline-none text-sm text-white placeholder:text-steel/80 w-full font-body"
            />
          </label>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <Reveal className="mt-16 text-center py-16 border border-white/5 bg-ink-800">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">No matches</div>
            <p className="mt-3 font-display text-2xl text-white">
              Try a different category or search term.
            </p>
          </Reveal>
        ) : (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {filtered.map((p, i) => (
              <Reveal
                key={p.id}
                delay={(i % 6) * 60}
                className="group relative bg-ink-800 p-7 md:p-8 hover:bg-ink-700 transition-colors duration-500 min-h-[240px] flex flex-col"
                data-testid={`project-card-${p.id}`}
              >
                {p.status === "Ongoing" && (
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.28em] text-gold border border-gold/40 px-2.5 py-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                    In Progress
                  </span>
                )}

                <div className="font-mono text-[10px] uppercase tracking-[0.26em] text-steel">
                  №&nbsp;{String(i + 1).padStart(3, "0")} · {p.category}
                </div>

                <h3 className="mt-6 font-display text-2xl md:text-[26px] text-white leading-tight tracking-tight">
                  {p.name}
                </h3>

                <div className="mt-auto pt-8 space-y-2 text-sm text-steel-light">
                  {p.client && (
                    <div className="flex items-center gap-2">
                      <Building size={13} className="text-gold/70 flex-shrink-0" />
                      <span>{p.client}</span>
                    </div>
                  )}
                  {p.location && (
                    <div className="flex items-center gap-2">
                      <MapPin size={13} className="text-gold/70 flex-shrink-0" />
                      <span>{p.location}</span>
                    </div>
                  )}
                </div>

                <ArrowUpRight
                  size={18}
                  className="absolute bottom-5 right-5 text-steel opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
                />
                <div className="absolute left-0 bottom-0 h-px bg-gold w-0 group-hover:w-full transition-all duration-700" />
              </Reveal>
            ))}
          </div>
        )}

        {/* Construction Partners */}
        <div className="mt-24">
          <Reveal>
            <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold">
              Executed in association with
            </div>
          </Reveal>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {partners.map((p, i) => (
              <Reveal key={p.name} delay={i * 100} className="bg-ink-800 p-8 md:p-10" data-testid={`partner-${i}`}>
                <div className="font-display text-2xl md:text-3xl text-white leading-tight">
                  {p.name}
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm text-steel-light">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                    {p.role}
                  </span>
                  <span>· {p.location}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
